import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { ArrowLeftIcon, ArrowRightIcon, ForwardIcon, ScissorsIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { LOG_TRANSACTION, SET_DAILY_TRANSACTION_TOTAL } from '../../../../graphql/production-mutations'
import { GET_DAILY_LEDGER_RECIPES } from '../../../../graphql/production-queries'
import toast from 'react-hot-toast'
import type { RecipeLedgerRow } from '../RecipeLogStep'
import { toBatch, batchToWeight, weightToBatch } from '../../utils'

type WasteReason = 'spoilage' | 'quality' | 'other'

const REASONS: { value: WasteReason; label: string; hint: string }[] = [
  { value: 'spoilage', label: 'Spoilage',      hint: 'Food went off during service' },
  { value: 'quality',  label: 'Quality issue', hint: 'Undercooked, wrong texture, failed check' },
  { value: 'other',    label: 'Other',         hint: 'Catch-all' },
]

interface WastedExpiredStepProps {
  recipes: RecipeLedgerRow[]
  date: string
  canLog: boolean
  onLogged: () => void
  onNext: () => void
  onBack: () => void
  isFirst: boolean
}

interface RowState {
  qty: string
  batchQty: string
  reason: WasteReason | ''
  saving: boolean
  editing: boolean
}

const defaultRow = (): RowState => ({ qty: '', batchQty: '', reason: '', saving: false, editing: false })

const WastedExpiredStep = ({ recipes, date, canLog, onLogged, onNext, onBack, isFirst }: WastedExpiredStepProps) => {
  const [rows, setRows] = useState<Record<number, RowState>>({})

  const getRow = (id: number): RowState => rows[id] ?? defaultRow()
  const setRow = (id: number, patch: Partial<RowState>) =>
    setRows((prev) => ({ ...prev, [id]: { ...getRow(id), ...patch } }))

  const onWeightChange = (id: number, value: string, outputQty: number | null | undefined) => {
    const w = parseFloat(value)
    const batchQty = !isNaN(w) && outputQty && outputQty > 0 ? String(weightToBatch(w, outputQty)) : ''
    setRow(id, { qty: value, batchQty })
  }

  const onBatchChange = (id: number, value: string, outputQty: number | null | undefined) => {
    const b = parseFloat(value)
    const qty = !isNaN(b) && outputQty && outputQty > 0 ? String(batchToWeight(b, outputQty)) : ''
    setRow(id, { batchQty: value, qty })
  }

  const [setTotal] = useMutation(SET_DAILY_TRANSACTION_TOTAL, {
    refetchQueries: [{ query: GET_DAILY_LEDGER_RECIPES, variables: { date } }],
    onError: (e) => toast.error(e.message),
  })

  const [logTx] = useMutation(LOG_TRANSACTION, {
    refetchQueries: [{ query: GET_DAILY_LEDGER_RECIPES, variables: { date } }],
    onError: (e) => toast.error(e.message),
  })

  const handleAdd = async (recipe: RecipeLedgerRow) => {
    const row = getRow(recipe.raw_material_id)
    const qty = parseFloat(row.qty)
    if (isNaN(qty) || qty <= 0) { toast.error('Enter a positive quantity'); return }
    if (!row.reason) { toast.error('Select a reason'); return }

    setRow(recipe.raw_material_id, { saving: true })
    try {
      await logTx({
        variables: {
          data: {
            raw_material_id: recipe.raw_material_id,
            transaction_type: 'wasted',
            ledger_date: date,
            quantity: qty,
            reason: row.reason,
          },
        },
      })
      setRow(recipe.raw_material_id, { qty: '', batchQty: '', reason: '', saving: false })
      toast.success(`Logged for ${recipe.raw_material_name}`)
      onLogged()
    } catch {
      setRow(recipe.raw_material_id, { saving: false })
    }
  }

  const handleUpdate = async (recipe: RecipeLedgerRow, quantity: number) => {
    setRow(recipe.raw_material_id, { saving: true })
    try {
      await setTotal({
        variables: {
          data: {
            raw_material_id: recipe.raw_material_id,
            ledger_date: date,
            transaction_type: 'wasted',
            quantity,
          },
        },
      })
      setRow(recipe.raw_material_id, { qty: '', batchQty: '', saving: false, editing: false })
      toast.success(quantity === 0 ? `Cleared ${recipe.raw_material_name}` : `Updated ${recipe.raw_material_name}`)
      onLogged()
    } catch {
      setRow(recipe.raw_material_id, { saving: false })
    }
  }

  const handleUpdateFromEdit = async (recipe: RecipeLedgerRow) => {
    const row = getRow(recipe.raw_material_id)
    const qty = parseFloat(row.qty)
    if (isNaN(qty) || qty < 0) { toast.error('Enter a valid quantity'); return }
    await handleUpdate(recipe, qty)
  }

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Food Wastage</h2>
        <p className="mt-1 text-sm text-gray-500">
          Log food that was thrown away or could not be served.
        </p>
      </div>

      {/* ── Section 1: Batches & Weight ────────────────────────────────────── */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Batches &amp; weight</h3>
          <p className="mt-0.5 text-xs text-gray-400">Log by recipe unit — full or partial batches.</p>
        </div>

        {recipes.length === 0 ? (
          <p className="text-sm text-gray-400">No tracked recipes found.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipe</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Already logged today</th>
                  {canLog && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Add entry</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {recipes.map((recipe) => {
                  const combined = (recipe.wasted_quantity ?? 0) + (recipe.expired_quantity ?? 0)
                  const batchCombined = toBatch(combined, recipe.output_quantity)
                  const row = getRow(recipe.raw_material_id)

                  return (
                    <tr key={recipe.raw_material_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{recipe.raw_material_name}</span>
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          {recipe.unit}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {combined > 0 ? (
                          <span className="inline-flex items-center gap-2">
                            <span>
                              <span className="block font-medium text-gray-900">{combined} {recipe.unit}</span>
                              {batchCombined !== null && (
                                <span className="block text-xs text-gray-400">{batchCombined} batch</span>
                              )}
                            </span>
                            {canLog && !row.editing && (
                              <button
                                onClick={() => setRow(recipe.raw_material_id, {
                                  editing: true,
                                  qty: String(combined),
                                  batchQty: recipe.output_quantity
                                    ? String(weightToBatch(combined, recipe.output_quantity))
                                    : '',
                                })}
                                className="text-gray-400 hover:text-primary-600 shrink-0"
                                title="Correct this value"
                              >
                                <PencilIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      {canLog && (
                        <td className="px-4 py-3">
                          {row.editing ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="flex items-center gap-1">
                                <input
                                  type="number" step="any" min="0"
                                  value={row.qty}
                                  onChange={(e) => onWeightChange(recipe.raw_material_id, e.target.value, recipe.output_quantity)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleUpdateFromEdit(recipe)}
                                  placeholder={recipe.unit}
                                  className="input w-24 text-sm py-1.5 border-amber-300 bg-amber-50"
                                  disabled={row.saving}
                                  autoFocus
                                />
                                <span className="text-xs text-gray-500">{recipe.unit}</span>
                              </div>
                              {recipe.output_quantity != null && recipe.output_quantity > 0 && (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number" step="any" min="0"
                                    value={row.batchQty}
                                    onChange={(e) => onBatchChange(recipe.raw_material_id, e.target.value, recipe.output_quantity)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleUpdateFromEdit(recipe)}
                                    placeholder="batch"
                                    className="input w-20 text-sm py-1.5 border-amber-300 bg-amber-50"
                                    disabled={row.saving}
                                  />
                                  <span className="text-xs text-gray-500">batch</span>
                                </div>
                              )}
                              <button
                                onClick={() => handleUpdateFromEdit(recipe)}
                                disabled={row.saving || row.qty === ''}
                                className="rounded-md bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-400 disabled:opacity-40"
                              >
                                {row.saving ? '…' : 'Update'}
                              </button>
                              <button
                                onClick={() => handleUpdate(recipe, 0)}
                                disabled={row.saving}
                                className="rounded-md border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40"
                              >
                                Clear
                              </button>
                              <button
                                onClick={() => setRow(recipe.raw_material_id, { editing: false, qty: '', batchQty: '' })}
                                disabled={row.saving}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="flex items-center gap-1">
                                <input
                                  type="number" step="any" min="0"
                                  value={row.qty}
                                  onChange={(e) => onWeightChange(recipe.raw_material_id, e.target.value, recipe.output_quantity)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleAdd(recipe)}
                                  placeholder={recipe.unit}
                                  className="input w-24 text-sm py-1.5"
                                  disabled={row.saving}
                                />
                                <span className="text-xs text-gray-500">{recipe.unit}</span>
                              </div>
                              {recipe.output_quantity != null && recipe.output_quantity > 0 && (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number" step="any" min="0"
                                    value={row.batchQty}
                                    onChange={(e) => onBatchChange(recipe.raw_material_id, e.target.value, recipe.output_quantity)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleAdd(recipe)}
                                    placeholder="batch"
                                    className="input w-20 text-sm py-1.5"
                                    disabled={row.saving}
                                  />
                                  <span className="text-xs text-gray-500">batch</span>
                                </div>
                              )}
                              <select
                                value={row.reason}
                                onChange={(e) => setRow(recipe.raw_material_id, { reason: e.target.value as WasteReason })}
                                className="input text-sm py-1.5"
                                disabled={row.saving}
                              >
                                <option value="">Reason…</option>
                                {REASONS.map((r) => (
                                  <option key={r.value} value={r.value} title={r.hint}>{r.label}</option>
                                ))}
                              </select>
                              <button
                                onClick={() => handleAdd(recipe)}
                                disabled={row.saving || !row.qty || !row.reason}
                                className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-500 disabled:opacity-40"
                              >
                                {row.saving ? '…' : 'Add'}
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Section 2: Portions ────────────────────────────────────────────── */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Portions</h3>
          <p className="mt-0.5 text-xs text-gray-400">Log individual portions that were wasted during service.</p>
        </div>
        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-6 py-8 text-center">
          <ScissorsIcon className="mx-auto h-8 w-8 text-gray-300 mb-2" />
          <p className="text-sm font-medium text-gray-400">Portion-level wastage logging</p>
          <p className="mt-1 text-xs text-gray-400">Coming soon — this section will let you log wasted portions by product variant.</p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <button
          onClick={onBack}
          disabled={isFirst}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-30"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={onNext}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50"
          >
            <ForwardIcon className="h-4 w-4" />
            Nothing to log
          </button>
          <button
            onClick={onNext}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-500"
          >
            Next
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default WastedExpiredStep
